/**
 * Every configurable value in the app, read once from import.meta.env.
 *
 * Names match scripts/seed-env.js, which is what writes these into SSM and
 * materializes .env; see .env.example. Nothing else in the app should touch
 * import.meta.env directly.
 */

function trimTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

const rawApiUrl = import.meta.env.VITE_API_URL ?? '';

export const config = {
  /** Origin of slate-api. Calls are made to `${apiUrl}/v1/...`. */
  apiUrl: trimTrailingSlash(rawApiUrl || 'http://localhost:3000'),

  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_CONFIG_APIKEY ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_CONFIG_AUTHDOMAIN ?? '',
    projectId: import.meta.env.VITE_FIREBASE_CONFIG_PROJECTID ?? '',
    storageBucket: import.meta.env.VITE_FIREBASE_CONFIG_STORAGEBUCKET ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_CONFIG_MESSAGINGSENDERID ?? '',
    appId: import.meta.env.VITE_FIREBASE_CONFIG_APPID ?? '',
  },

  /**
   * GA4, the property attached to the Firebase project. The marketing site
   * reports to the same one, so a visit that starts on an ad and ends in a
   * signup here is one journey. Empty in local builds: nothing loads.
   */
  gaMeasurementId: import.meta.env.VITE_FIREBASE_CONFIG_MEASUREMENTID ?? '',

  /** Amplitude project key for this environment. Empty: analytics off. */
  amplitudeApiKey: import.meta.env.VITE_AMPLITUDE_API_KEY ?? '',

  /** ~2.5 Mbps, tuned for 1080p screen content. */
  recorderBitrate: Number(import.meta.env.VITE_RECORDER_BITRATE) || 2_500_000,
} as const;

/** Is Firebase configured well enough to attempt auth? */
export const firebaseConfigured = Boolean(config.firebase.apiKey && config.firebase.projectId);

/**
 * Where the app itself lives, whatever address the browser reached it by.
 *
 * A team on their own domain is served this same bundle from that domain, so
 * `window.location.origin` is not a reliable answer to "where is Slate". This
 * is configured per environment (VITE_PUBLIC_ORIGIN) and falls back to the
 * current origin only where nothing was configured, which is a local build.
 */
export function publicOrigin(): string {
  const override = import.meta.env.VITE_PUBLIC_ORIGIN;
  if (override) return trimTrailingSlash(override);
  return window.location.origin;
}

/**
 * Is this page being served from a team's own domain rather than from Slate?
 *
 * A custom domain serves share pages and nothing else. Everything behind an
 * account belongs on the one origin: Firebase trusts a fixed list of domains,
 * and a session is per-origin, so signing in anywhere else would be both
 * broken for Google and a second session besides.
 */
export function onCustomDomain(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.origin !== publicOrigin();
}

/** The same path, on the origin the app actually lives at. */
export function canonicalUrl(path: string): string {
  return `${publicOrigin()}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Paths a team's own domain serves: the share page and the request-link page,
 * the two a viewer of theirs can land on. Everything else is the app.
 */
export function servedOnCustomDomain(pathname: string): boolean {
  return pathname.startsWith('/v/') || pathname.startsWith('/r/');
}

/**
 * A link to hand somebody. `domain` is a team's own hostname, when they have
 * one serving (custom branding); without one this is the Slate address, and
 * both keep working forever either way, so a link already sent is never
 * invalidated by a team adding or dropping a domain.
 */
export function shareUrl(shareId: string, domain?: string | null): string {
  const origin = domain ? `https://${domain}` : publicOrigin();
  return `${origin}/v/${shareId}`;
}

export function requestLinkUrl(token: string): string {
  return `${publicOrigin()}/r/${token}`;
}
